import { useEffect } from 'react';
import cn from 'classnames';
import styles from './styles.scss';
import { LoaderProps, isLoading } from 'components/Shared/Loader';
import { ButtonElements, AppearanceTypes, ColorType } from 'components/Shared/Button';
import { useToggle, useMounted } from 'hooks';
import { getQaDataAttr, QaDataAttribute } from '@bygghemmait/utilities';

type ClickHandle = () => Promise<unknown | void | undefined>;
type SizeType = 'large' | 'small' | 'tiny';

export interface LoaderButtonProps extends LoaderProps {
  appearance?: AppearanceTypes;
  colorType?: ColorType;
  size?: SizeType;
  onClick?: ClickHandle | ((e: React.MouseEvent<ButtonElements>) => void);
  href?: string;
  inverted?: boolean;
  type?: 'submit' | 'button';
  disabled?: boolean;
  qaAttr?: QaDataAttribute;
}

/***
 * @deprecated - Only intended for use in legacy bunldes, for RED3, use components/Button instead
 */
export default function LoaderButton({
  onClick,
  className,
  children,
  appearance,
  size,
  href,
  type,
  inverted,
  colorType,
  loading,
  disabled,
  qaAttr
}: LoaderButtonProps) {
  const isMounted = useMounted();
  const [feedback, setFeedback] = useToggle(isLoading(loading));
  useEffect(() => {
    setFeedback(isLoading(loading));
  }, [loading, setFeedback]);
  const handlePromise = () => {
    isMounted() && setFeedback(false);
  };
  const handleOnClick = (handle: ClickHandle) => () => {
    setFeedback(() => true);
    const promise = handle();
    if (!(promise instanceof Promise)) {
      return Promise.resolve().then(handlePromise);
    }
    return promise.finally(handlePromise);
  };

  const isDisabled = disabled || feedback || isLoading(loading);

  const _className = cn(
    {
      [styles.primary]: !appearance || appearance === 'primary',
      [styles.secondary]: appearance === 'secondary',
      [styles.tertiary]: appearance === 'tertiary',
      [styles.quaternary]: appearance === 'quaternary',
      [styles.large]: size === 'large',
      [styles.small]: size === 'small',
      [styles.inverted]: inverted,
      [styles.clear]: appearance === 'clear',
      [styles.loading]: feedback || isLoading(loading),
      [styles.blue]: !colorType || colorType === 'blue',
      [styles.grey]: colorType === 'grey',
      [styles.disabled]: isDisabled
    },
    className
  );
  const dataQaAttr = qaAttr ? getQaDataAttr(qaAttr) : undefined;
  return href !== undefined ? (
    <a {...{ className: _className, onClick, disabled: isDisabled, href, ...dataQaAttr }}>
      {children}
    </a>
  ) : (
    <button
      type={type || 'button'}
      {...{
        className: _className,
        onClick: onClick ? handleOnClick(onClick as ClickHandle) : undefined,
        disabled: isDisabled,
        ...dataQaAttr
      }}
    >
      {children}
    </button>
  );
}
